import styled, { css } from 'styled-components';
import { useAtomValue } from 'jotai';
import { FlexCol, ItemFlex1 } from 'components/primitives/flex';
import { sortMessages } from 'stacks/messaging/helpers';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import Firebase from 'stacks/messaging/Firebase';
import { useQueryClient } from 'react-query';
import { mediaMMdMin, mediaMSmMax } from 'helpers/breakpoints';
import { renderMessage } from 'components/inbox/messages';
import InfiniteScroll from 'react-infinite-scroll-component';
import ProtectInfoMessage from 'components/inbox/messages/ProtectInfoMessage';
import { ConversationMessage } from 'swagger/models';
import { useUserDetails } from 'auth/mutations/userHooks';
import InfoBanner from 'components/dashboard/renterInsurance/components/InfoBanner';
import { panelMobileStyles, panelScrollMixin } from './commons';
import MessageInputBar from './MessageInputBar';
import { activeTabNameAtom, mobileConversationDetailVisibleAtom, sentSpamMessageAtom } from './store';
import useConversationDetail from './hooks/useConversationDetail';
import useReadArchiveMessageMutation from './hooks/useReadArchiveMessageMutation';
import ConversationHeader from './ConversationHeader';
import useMessagesFilterSelectedValues from './hooks/useMessagesFilterSelectedValues';
import { useShowAdminView } from './hooks/useAdminView';
import DefaultMessage from './messages/DefaultMessage';
import useEditBookingModal from './bookingPanel/useEditBookingModal';
import { isPreApprovable } from './BookingBooleansHelpers';
import ActionCards from './ActionCards';
import { useGetBookingActions } from './hooks/useGetBookingActions';

export default function ConversationDetail() {
  const isMobileDetailVisible = useAtomValue(mobileConversationDetailVisibleAtom);
  const queryClient = useQueryClient();
  const activeTabName = useAtomValue(activeTabNameAtom);
  const { isArchiveFilterSelected } = useMessagesFilterSelectedValues();
  const showAdminView = useShowAdminView();
  const sentSpamMessage = useAtomValue(sentSpamMessageAtom);
  const { userId, userDetails } = useUserDetails();
  const impersonated = userDetails.is_impersonating;

  const {
    conversationId: activeConversationId,
    fetchNextPage,
    hasNextPage,
    pages,
    data: { read, booking },
    data,
  } = useConversationDetail({
    onSuccess: () => {
      if (!read && !showAdminView && !impersonated) {
        readArchiveMessageMutation.mutate({});
      }
    },
  });
  const { actions, isLoading: isFetchingActions } = useGetBookingActions(booking?.id);

  const readArchiveMessageMutation = useReadArchiveMessageMutation(activeConversationId, booking?.id);

  const scrollEl = useRef(null);
  useEffect(() => {
    // scroll down in conversation by default
    if (scrollEl.current) {
      scrollEl.current.scrollTop = scrollEl.current.scrollHeight;
    }
  }, [activeConversationId, isFetchingActions]);

  // height of scrollable container lagged by 1 page
  const prevHeightRef = useRef(0);
  // scroll pixels before fetching data
  const prevScrollTopRef = useRef(0);

  // keep same amount of scroll pixel of #scrollableDiv, hold viewport when new messages appear
  useLayoutEffect(() => {
    if (scrollEl.current && pages.length > 0) {
      scrollEl.current.scrollTop =
        scrollEl.current.scrollHeight -
        prevHeightRef.current +
        // if user scroll to top before query finished fetching we don't want to add previously cached scrollTop position
        // 120 is average height of message
        (scrollEl.current.scrollTop === 0 ? 0 : prevScrollTopRef.current - 120);
    }
    prevHeightRef.current = scrollEl.current.scrollHeight;
  }, [pages.length]);

  const joinedMessages: ConversationMessage[] = pages.reduce(
    (prev, current) => [...prev, ...(current.data.messages || [])],
    []
  );

  const sortedMessages: ConversationMessage[] = sortMessages(joinedMessages ?? []);

  const { modal: editBookingModal, openModal: openEditBookingModal } = useEditBookingModal(booking);
  const renderedMessage = renderMessage(
    {
      ...data,
      messages: joinedMessages,
    },
    userId,
    openEditBookingModal
  );

  const ownerHasNotResponded = data.info_messages.some(
    (info_message) => info_message.message_type === 'owner_has_not_responded'
  );
  const userTypeOwner = data.my_user_type_in_conversation === 'owner';
  const notPreApprovable = !isPreApprovable(booking);
  const displayRespondReminder = ownerHasNotResponded && userTypeOwner && notPreApprovable;

  const renderOwnerRespondReminder = useCallback(() => {
    if (displayRespondReminder) {
      return (
        <InfoBanner
          blueVariant
          text="Don’t forget to reply back. Guests are more likely to book when Owners respond to their message."
        />
      );
    }
    return null;
  }, [displayRespondReminder]);

  return (
    <Container isVisibleOnMobile={isMobileDetailVisible}>
      <ConversationHeader conversation={data} />
      <ContentWrapper>
        <Content ref={scrollEl} id="scrollableDiv">
          <StyledInfiniteScroll
            next={() => {
              prevScrollTopRef.current = scrollEl.current.scrollTop;
              fetchNextPage();
            }}
            hasMore={hasNextPage}
            loader="Loading"
            inverse
            scrollThreshold={0.2}
            scrollableTarget="scrollableDiv"
            dataLength={sortedMessages.length}
          >
            <ProtectInfoMessage />
            {sortedMessages?.map(renderedMessage)}
            {sentSpamMessage.body && sentSpamMessage.conversation_id === activeConversationId && (
              <DefaultMessage isSpam message={sentSpamMessage} />
            )}
            {renderOwnerRespondReminder()}
            <ActionCards booking={booking} actions={actions} />
          </StyledInfiniteScroll>
        </Content>
      </ContentWrapper>

      <MessageInputBar />

      {userId && activeConversationId ? (
        <Firebase
          userId={userId}
          conversationId={activeConversationId}
          handleChange={() => {
            queryClient.invalidateQueries(['conversationDetail', activeConversationId]);
            queryClient.invalidateQueries(['conversationsTab', activeTabName, userId, isArchiveFilterSelected]);
          }}
        />
      ) : null}

      {editBookingModal}
    </Container>
  );
}

const StyledInfiniteScroll = styled(InfiniteScroll)`
  overflow: hidden !important;
`;

export const Container = styled(FlexCol)<{ isVisibleOnMobile?: boolean }>`
  height: 100%;
  overflow: hidden;

  ${mediaMSmMax} {
    ${panelMobileStyles}

    transform: translateX(100%);
    transition: all 0.3s;

    ${({ isVisibleOnMobile }) =>
      isVisibleOnMobile &&
      css`
        transform: translateX(0%);
      `};
  }
  ${mediaMMdMin} {
    border-left: 1px solid #dbdfe5;
    border-right: 1px solid #dbdfe5;
  }
`;

const ContentWrapper = styled(ItemFlex1)`
  height: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  padding: 30px 36px;
  ${panelScrollMixin}
`;
